import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { workspacePath } from "../lib/workspaces";
import { apiGet, apiPost, apiPatch, apiDelete } from "../lib/api";
import {
  KeyRound,
  Webhook,
  Trash2,
  Loader2,
  Copy,
  Check,
  Plus,
  Sparkles,
  Send,
  ChevronDown,
  ChevronRight,
  RotateCcw,
} from "lucide-react";

const API_BASE = `${window.location.origin}/api/v1`;

interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

interface WebhookEndpoint {
  id: string;
  url: string;
  events: string[];
  description: string | null;
  enabled: boolean;
  disabledAt: string | null;
  lastError: string | null;
  lastAttemptAt: string | null;
  lastSuccessAt: string | null;
  createdAt: string;
}

interface Delivery {
  id: string;
  event: string;
  status: string;
  attempts: number;
  responseStatus: number | null;
  error: string | null;
  nextAttemptAt: string | null;
  deliveredAt: string | null;
  createdAt: string;
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="p-1.5 text-stone-400 hover:text-stone-700 hover:bg-stone-100 rounded-lg transition-colors"
      title="Copy"
    >
      {copied ? <Check size={15} className="text-green-600" /> : <Copy size={15} />}
    </button>
  );
}

export function Developers() {
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [endpoints, setEndpoints] = useState<WebhookEndpoint[]>([]);
  const [availableEvents, setAvailableEvents] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    const [keysRes, hooksRes] = await Promise.all([
      apiGet<ApiKey[]>("/api-keys"),
      apiGet<{ events: string[]; endpoints: WebhookEndpoint[] }>("/webhooks"),
    ]);
    if (keysRes.error?.key === "PLAN_REQUIRED") {
      setGated(true);
      setLoading(false);
      return;
    }
    if (keysRes.success && keysRes.data) setKeys(keysRes.data);
    if (hooksRes.success && hooksRes.data) {
      setEndpoints(hooksRes.data.endpoints);
      setAvailableEvents(hooksRes.data.events);
    }
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-brand-500 animate-spin" />
      </div>
    );
  }

  if (gated) return <UpgradeGate />;

  return (
    <div className="p-4 sm:p-6 max-w-3xl space-y-8">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold text-stone-900 font-display">Developers</h1>
        <p className="text-sm text-stone-500 mt-1">
          Pull check-in data and receive real-time events. Manage your API keys and webhooks below.
        </p>
      </div>

      {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

      <ApiKeysSection keys={keys} setKeys={setKeys} setError={setError} />
      <WebhooksSection
        endpoints={endpoints}
        setEndpoints={setEndpoints}
        availableEvents={availableEvents}
        setError={setError}
      />
      <DocsSection events={availableEvents} />
    </div>
  );
}

// ── API keys ──

function ApiKeysSection({
  keys,
  setKeys,
  setError,
}: {
  keys: ApiKey[];
  setKeys: React.Dispatch<React.SetStateAction<ApiKey[]>>;
  setError: (s: string | null) => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);

  async function create(e: FormEvent) {
    e.preventDefault();
    setCreating(true);
    setError(null);
    const res = await apiPost<ApiKey & { key: string }>("/api-keys", { name });
    if (res.success && res.data) {
      setKeys((prev) => [res.data!, ...prev]);
      setNewKey(res.data.key);
      setName("");
      setShowForm(false);
    } else {
      setError(res.error?.message || "Failed to create key");
    }
    setCreating(false);
  }

  async function revoke(k: ApiKey) {
    if (!confirm(`Revoke "${k.name}"? Any integration using it will stop working immediately.`)) return;
    const res = await apiDelete(`/api-keys/${k.id}`);
    if (res.success) {
      setKeys((prev) => prev.map((x) => (x.id === k.id ? { ...x, revokedAt: new Date().toISOString() } : x)));
    } else {
      setError(res.error?.message || "Failed to revoke key");
    }
  }

  const active = keys.filter((k) => !k.revokedAt);

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <KeyRound size={18} className="text-stone-500" />
          <h2 className="text-base font-semibold text-stone-900">API Keys</h2>
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
        >
          <Plus size={15} />
          New Key
        </button>
      </div>

      {newKey && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-3">
          <p className="text-sm font-medium text-amber-900 mb-1">Copy your API key now</p>
          <p className="text-xs text-amber-700 mb-2">
            This is the only time we'll show it. Store it securely — it won't be retrievable again.
          </p>
          <div className="flex items-center gap-1 bg-white border border-amber-200 rounded-lg px-3 py-2">
            <code className="text-xs text-stone-800 break-all flex-1">{newKey}</code>
            <CopyButton value={newKey} />
          </div>
          <button
            onClick={() => setNewKey(null)}
            className="mt-2 text-xs font-medium text-amber-800 hover:text-amber-900"
          >
            Done
          </button>
        </div>
      )}

      {showForm && (
        <form onSubmit={create} className="bg-white rounded-xl border border-stone-200 p-4 mb-3 space-y-3">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Key name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="e.g. Production CRM sync"
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={creating}
              className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50"
            >
              {creating && <Loader2 size={14} className="animate-spin" />}
              Create Key
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-stone-600 hover:text-stone-900">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-xl border border-stone-200 divide-y divide-stone-100">
        {keys.map((k) => (
          <div key={k.id} className="flex items-center justify-between px-5 py-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-stone-900">
                {k.name}
                {k.revokedAt && <span className="ml-2 text-xs text-red-500">revoked</span>}
              </p>
              <p className="text-xs text-stone-500 font-mono mt-0.5">{k.prefix}…</p>
              <p className="text-xs text-stone-400 mt-0.5">
                Created {new Date(k.createdAt).toLocaleDateString()} ·{" "}
                {k.lastUsedAt ? `Last used ${new Date(k.lastUsedAt).toLocaleDateString()}` : "Never used"}
              </p>
            </div>
            {!k.revokedAt && (
              <button
                onClick={() => revoke(k)}
                className="p-2 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Revoke key"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        ))}
        {active.length === 0 && keys.length === 0 && (
          <div className="px-5 py-8 text-center text-sm text-stone-400">
            No API keys yet. Create one to start using the API.
          </div>
        )}
      </div>
    </section>
  );
}

// ── Webhooks ──

function WebhooksSection({
  endpoints,
  setEndpoints,
  availableEvents,
  setError,
}: {
  endpoints: WebhookEndpoint[];
  setEndpoints: React.Dispatch<React.SetStateAction<WebhookEndpoint[]>>;
  availableEvents: string[];
  setError: (s: string | null) => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [url, setUrl] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [newSecret, setNewSecret] = useState<string | null>(null);

  function toggleEvent(ev: string) {
    setSelected((prev) => (prev.includes(ev) ? prev.filter((e) => e !== ev) : [...prev, ev]));
  }

  async function create(e: FormEvent) {
    e.preventDefault();
    if (selected.length === 0) {
      setError("Select at least one event");
      return;
    }
    setCreating(true);
    setError(null);
    const res = await apiPost<WebhookEndpoint & { secret: string }>("/webhooks", { url, events: selected });
    if (res.success && res.data) {
      setEndpoints((prev) => [res.data!, ...prev]);
      setNewSecret(res.data.secret);
      setUrl("");
      setSelected([]);
      setShowForm(false);
    } else {
      setError(res.error?.message || "Failed to create webhook");
    }
    setCreating(false);
  }

  async function toggleEnabled(ep: WebhookEndpoint) {
    const res = await apiPatch<WebhookEndpoint>(`/webhooks/${ep.id}`, { enabled: !ep.enabled });
    if (res.success && res.data) {
      setEndpoints((prev) => prev.map((x) => (x.id === ep.id ? res.data! : x)));
    } else {
      setError(res.error?.message || "Failed to update webhook");
    }
  }

  async function remove(ep: WebhookEndpoint) {
    if (!confirm(`Delete webhook for ${ep.url}?`)) return;
    const res = await apiDelete(`/webhooks/${ep.id}`);
    if (res.success) {
      setEndpoints((prev) => prev.filter((x) => x.id !== ep.id));
    } else {
      setError(res.error?.message || "Failed to delete webhook");
    }
  }

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Webhook size={18} className="text-stone-500" />
          <h2 className="text-base font-semibold text-stone-900">Webhooks</h2>
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
        >
          <Plus size={15} />
          Add Endpoint
        </button>
      </div>

      {newSecret && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-3">
          <p className="text-sm font-medium text-amber-900 mb-1">Signing secret</p>
          <p className="text-xs text-amber-700 mb-2">
            Use this to verify the <code>X-VMS-Signature</code> header on incoming events. Shown once.
          </p>
          <div className="flex items-center gap-1 bg-white border border-amber-200 rounded-lg px-3 py-2">
            <code className="text-xs text-stone-800 break-all flex-1">{newSecret}</code>
            <CopyButton value={newSecret} />
          </div>
          <button onClick={() => setNewSecret(null)} className="mt-2 text-xs font-medium text-amber-800 hover:text-amber-900">
            Done
          </button>
        </div>
      )}

      {showForm && (
        <form onSubmit={create} className="bg-white rounded-xl border border-stone-200 p-4 mb-3 space-y-3">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Endpoint URL</label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
              placeholder="https://your-server.com/webhooks/singvms"
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1.5">Events</label>
            <div className="space-y-1.5">
              {availableEvents.map((ev) => (
                <label key={ev} className="flex items-center gap-2 text-sm text-stone-700">
                  <input
                    type="checkbox"
                    checked={selected.includes(ev)}
                    onChange={() => toggleEvent(ev)}
                    className="rounded border-stone-300 text-brand-500 focus:ring-brand-500"
                  />
                  <code className="text-xs">{ev}</code>
                </label>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={creating}
              className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50"
            >
              {creating && <Loader2 size={14} className="animate-spin" />}
              Add Endpoint
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-stone-600 hover:text-stone-900">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {endpoints.map((ep) => (
          <WebhookRow
            key={ep.id}
            ep={ep}
            onToggle={() => toggleEnabled(ep)}
            onDelete={() => remove(ep)}
            setError={setError}
          />
        ))}
        {endpoints.length === 0 && (
          <div className="bg-white rounded-xl border border-stone-200 px-5 py-8 text-center text-sm text-stone-400">
            No webhook endpoints yet.
          </div>
        )}
      </div>
    </section>
  );
}

function WebhookRow({
  ep,
  onToggle,
  onDelete,
  setError,
}: {
  ep: WebhookEndpoint;
  onToggle: () => void;
  onDelete: () => void;
  setError: (s: string | null) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [deliveries, setDeliveries] = useState<Delivery[] | null>(null);
  const [testing, setTesting] = useState(false);
  const [testMsg, setTestMsg] = useState<string | null>(null);
  const [redelivering, setRedelivering] = useState<string | null>(null);

  async function loadDeliveries() {
    const res = await apiGet<Delivery[]>(`/webhooks/${ep.id}/deliveries`);
    if (res.success && res.data) setDeliveries(res.data);
  }

  function toggleExpand() {
    const next = !expanded;
    setExpanded(next);
    if (next && deliveries === null) loadDeliveries();
  }

  async function sendTest() {
    setTesting(true);
    setTestMsg(null);
    const res = await apiPost(`/webhooks/${ep.id}/test`, {});
    if (res.success) {
      setTestMsg("Test event queued — check recent deliveries.");
      setTimeout(loadDeliveries, 1500);
      if (!expanded) toggleExpand();
    } else {
      setError(res.error?.message || "Failed to send test");
    }
    setTesting(false);
  }

  async function redeliver(delivery: Delivery) {
    setRedelivering(delivery.id);
    setError(null);
    const res = await apiPost(`/webhooks/${ep.id}/deliveries/${delivery.id}/redeliver`, {});
    if (res.success) {
      setTestMsg("Redelivery queued with a new delivery ID.");
      await loadDeliveries();
    } else {
      setError(res.error?.message || "Failed to queue redelivery");
    }
    setRedelivering(null);
  }

  return (
    <div className="bg-white rounded-xl border border-stone-200">
      <div className="flex items-center justify-between px-5 py-4 gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-stone-900 truncate">{ep.url}</p>
          <p className="text-xs text-stone-500 mt-0.5 flex flex-wrap gap-1">
            {ep.events.map((e) => (
              <code key={e} className="bg-stone-100 px-1.5 py-0.5 rounded text-[11px]">{e}</code>
            ))}
          </p>
          {!ep.enabled && (
            <p className="text-xs text-red-500 mt-1">
              Disabled{ep.disabledAt ? " automatically after repeated failures" : ""}
              {ep.lastError ? ` · ${ep.lastError}` : ""}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={sendTest}
            disabled={testing}
            className="p-2 text-stone-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors disabled:opacity-50"
            title="Send test event"
          >
            {testing ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
          <button
            onClick={onToggle}
            className={`px-2.5 py-1 text-xs font-medium rounded-lg transition-colors ${
              ep.enabled
                ? "text-stone-600 hover:bg-stone-100"
                : "text-brand-700 bg-brand-50 hover:bg-brand-100"
            }`}
          >
            {ep.enabled ? "Disable" : "Enable"}
          </button>
          <button
            onClick={onDelete}
            className="p-2 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      <button
        onClick={toggleExpand}
        className="flex items-center gap-1 px-5 py-2 border-t border-stone-100 text-xs font-medium text-stone-500 hover:text-stone-800 w-full"
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        Recent deliveries
      </button>

      {expanded && (
        <div className="px-5 pb-4">
          {testMsg && <p className="text-xs text-green-600 mb-2">{testMsg}</p>}
          {deliveries === null ? (
            <p className="text-xs text-stone-400 py-2">Loading…</p>
          ) : deliveries.length === 0 ? (
            <p className="text-xs text-stone-400 py-2">No deliveries yet.</p>
          ) : (
            <div className="divide-y divide-stone-100">
              {deliveries.map((d) => (
                <div key={d.id} className="flex items-center justify-between py-2 text-xs gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={`inline-block w-2 h-2 rounded-full shrink-0 ${
                        d.status === "SUCCESS"
                          ? "bg-green-500"
                          : d.status === "FAILED"
                            ? "bg-red-500"
                            : "bg-amber-400"
                      }`}
                    />
                    <code className="text-stone-700">{d.event}</code>
                    <span className="text-stone-400">{new Date(d.createdAt).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-stone-500">
                      {d.responseStatus ? `HTTP ${d.responseStatus}` : d.error ? "error" : d.status.toLowerCase()}
                      {d.attempts > 1 ? ` · ${d.attempts} tries` : ""}
                    </span>
                    <button
                      onClick={() => redeliver(d)}
                      disabled={!ep.enabled || redelivering === d.id}
                      className="p-1.5 text-stone-400 hover:text-brand-600 hover:bg-brand-50 rounded-md disabled:opacity-40 disabled:hover:bg-transparent"
                      title={ep.enabled ? "Redeliver with a new delivery ID" : "Enable the endpoint before redelivery"}
                    >
                      {redelivering === d.id ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Docs ──

function DocsSection({ events }: { events: string[] }) {
  const verifyExample = `// Verify the X-VMS-Signature header (Node.js)
import crypto from "node:crypto";

function verify(rawBody, header, secret) {
  if (!header) return false;
  const parts = Object.fromEntries(header.split(",").map((p) => p.split("=")));
  const expected = crypto
    .createHmac("sha256", secret)
    .update(\`\${parts.t}.\${rawBody}\`)
    .digest("hex");
  const a = Buffer.from(parts.v1 || "", "hex");
  const b = Buffer.from(expected, "hex");
  return a.length === b.length && crypto.timingSafeEqual(a, b);
}`;

  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold text-stone-900">API reference</h2>
      <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-4 text-sm">
        <div>
          <p className="font-medium text-stone-800">Base URL</p>
          <code className="text-xs text-stone-600">{API_BASE}</code>
        </div>
        <div>
          <p className="font-medium text-stone-800">Authentication</p>
          <p className="text-stone-600 text-xs mt-0.5">Send your API key as a Bearer token:</p>
          <pre className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-xs text-stone-700 overflow-x-auto mt-1">
{`Authorization: Bearer vms_live_xxxx_xxxxxxxx`}
          </pre>
        </div>
        <div>
          <p className="font-medium text-stone-800">Endpoints</p>
          <ul className="text-xs text-stone-600 space-y-1 mt-1 font-mono">
            <li>GET /me</li>
            <li>GET /checkins?locationId=&amp;from=&amp;to=&amp;page=&amp;pageSize=</li>
            <li>GET /checkins/:id</li>
            <li>GET /checkins/by-external-ref/:externalRef</li>
            <li>PATCH /checkins/:id</li>
            <li>POST /checkins/:id/checkout</li>
            <li>GET /locations</li>
            <li>GET /locations/:id</li>
          </ul>
        </div>
        <div>
          <p className="font-medium text-stone-800">Webhook events</p>
          <p className="text-xs text-stone-600 mt-0.5">
            {events.length ? events.join(", ") : "—"}. Each POST includes headers{" "}
            <code>X-VMS-Event</code>, <code>X-VMS-Delivery</code>, and <code>X-VMS-Signature</code>. Respond with a 2xx
            within 10s; non-2xx responses are retried with exponential backoff.
          </p>
        </div>
        <div>
          <p className="font-medium text-stone-800 mb-1">Verifying signatures</p>
          <pre className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-xs text-stone-700 overflow-x-auto">
{verifyExample}
          </pre>
        </div>
      </div>
    </section>
  );
}

// ── Upgrade gate (FREE plan) ──

function UpgradeGate() {
  return (
    <div className="p-4 sm:p-6 max-w-2xl">
      <h1 className="text-xl sm:text-2xl font-semibold text-stone-900 font-display mb-4">Developers</h1>
      <div className="bg-gradient-to-br from-brand-50 to-white border border-brand-100 rounded-2xl p-8 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-100 mb-4">
          <Sparkles size={22} className="text-brand-600" />
        </div>
        <h2 className="text-lg font-semibold text-stone-900 mb-2">API access is a Professional feature</h2>
        <p className="text-sm text-stone-600 max-w-md mx-auto mb-5">
          Upgrade to pull check-in data into your CRM and receive real-time webhook events for every check-in and
          check-out. Self-serve API keys and webhooks unlock on the Professional plan and above.
        </p>
        <Link
          to={workspacePath("billing")}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
        >
          <Sparkles size={16} />
          Upgrade to enable
        </Link>
      </div>
    </div>
  );
}
